import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import MenuElementsList from 'components/Layout/_components/MenuElementsList/MenuElementsList';
import MenuSection from 'components/Layout/_components/MenuSection';
import checkAccess from 'config/permissions/checkAccess';
import compareWithRoutingPermissions from 'config/permissions/compareWithRoutingPermissions';
import useOrganizationPaymentStageInfo from 'hooks/useOrganizationPaymentStageInfo/useOrganizationPaymentStageInfo';
import menu_messages from 'messages/menu_messages';
import userDataStorage from 'storages/userDataStorage';

import menuElements from '../menuElements';

const OrganizationSection = () => {
  const { t } = useTranslation();
  // @ts-ignore
  const { ps_roles, roles, is_asset_owner, is_delegated } = userDataStorage.get() || {
    ps_roles: [],
    roles: [],
    is_asset_owner: false,
    is_delegated: false,
  };

  const { stage } = useOrganizationPaymentStageInfo();

  const elementsToDisplay = useMemo(() => {
    const userRoles = [...ps_roles, ...roles];

    const pathsInPocketSafeAdminWithPSAccess = menuElements.pocketSafeAdmin
      .filter(el => el.to && el.allowedForPSRoles?.length && checkAccess(ps_roles, el.allowedForPSRoles))
      .map(el => el.to);

    const permissionChecked = menuElements.organization.filter(element => {
      if (element.allowedForPSRoles?.length) return false;
      if (element.to && pathsInPocketSafeAdminWithPSAccess.includes(element.to)) return false;

      const hasFlagRequirement = Boolean(element.requiresAssetOwner || element.requiresIsDelegated);
      const hasFlagAccess = (element.requiresAssetOwner && is_asset_owner) || (element.requiresIsDelegated && is_delegated);
      if (hasFlagAccess) return true;

      const hasRoleRestrictions = element.allowedForPSRoles?.length || element.allowedForOrganizationRoles?.length;
      if (hasFlagRequirement && !hasRoleRestrictions) {
        return false;
      }
      let hasAccess = false;
      if (hasRoleRestrictions) {
        const allowedRoles = [...(element.allowedForOrganizationRoles || []), ...(element.allowedForPSRoles || [])];
        hasAccess = checkAccess(userRoles, allowedRoles);
      } else if (element.to) {
        hasAccess = compareWithRoutingPermissions(userRoles, element.to);
      } else {
        const allowedRoles = [...(element.allowedForOrganizationRoles || []), ...(element.allowedForPSRoles || [])];
        hasAccess = checkAccess(userRoles, allowedRoles);
      }
      return hasAccess;
    });

    // check organization payment stage
    const result = permissionChecked.map(element => {
      const { allowedInStages } = element;
      if (!allowedInStages || !stage) {
        return element;
      }
      return { ...element, disabled: !checkAccess([stage], allowedInStages) };
    });

    // Sort alphabetically by translated label
    return result.sort((a, b) => {
      const labelA = t(a.label).toLowerCase();
      const labelB = t(b.label).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [ps_roles, roles, stage, is_asset_owner, is_delegated, t]);

  if (!elementsToDisplay.length) return null;

  return (
    // @ts-ignore
    <MenuSection title={t(...menu_messages.organization)}>
      <MenuElementsList elements={elementsToDisplay} />
    </MenuSection>
  );
};

export default OrganizationSection;
